import { useCallback, useState } from 'react';

/**
 * Hook to detect intentional hover events by requiring a minimum hover duration.
 * Filters out accidental mouse movements and casual hovering.
 *
 * @param callback - Function to call when intentional hover is detected
 * @param delay - Minimum hover duration in milliseconds (default: 500ms)
 * @returns Object with onMouseEnter and onMouseLeave handlers
 */
export const useIntentionalHover = (callback: () => void, delay = 500) => {
	const [timer, setTimer] = useState<number | null>(null);

	const onMouseEnter = useCallback(() => {
		// Clear any existing timer
		if (timer) {
			clearTimeout(timer);
		}

		// Set new timer for intentional hover detection
		const newTimer = setTimeout(() => {
			callback();
			setTimer(null);
		}, delay);

		setTimer(newTimer);
	}, [callback, delay, timer]);

	const onMouseLeave = useCallback((): void => {
		// Cancel timer if user leaves before threshold
		if (timer) {
			clearTimeout(timer);
			setTimer(null);
		}
	}, [timer]);

	// Cleanup on unmount
	const cleanup = useCallback((): void => {
		if (timer) {
			clearTimeout(timer);
			setTimer(null);
		}
	}, [timer]);

	return {
		onMouseEnter,
		onMouseLeave,
		cleanup, // For manual cleanup if needed
	};
};
